import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import api from "../../../api";
import { formatMonthKey } from "./performanceFormat";
import EntryContextFields, { contextFieldsOf, contextValidationError } from "./EntryContextFields";

// "Enter Monthly Data" for a manual KPI. Fields and their labels come from
// the KPI's configured entry fields (kpi_manual_evidence_field_definitions,
// via GET /performance/my-kra-kpi/:kpiId). Saved through the existing KPI
// evidence workflow: draft -> (attachments) -> submitted for review. The
// value only counts toward the score once the reviewer approves it.
const ManualEntryModal = ({ kpi, month, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Phase 65: schools / category / subcategories / feedback recipients, for
  // KPIs configured with CONTEXT entry fields; keyed by field_key.
  const [context, setContext] = useState({});
  const [initialRecipients, setInitialRecipients] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/performance/my-kra-kpi/${kpi.kpi_id}`, { params: { month } });
        if (cancelled) return;
        const detail = res.data?.data || {};
        setForm(detail.entry_form || null);
        // A draft / returned entry re-opens with its saved context.
        const saved = detail.entry_context;
        if (saved && ["DRAFT", "NEEDS_CHANGES"].includes(saved.status)) {
          const next = {};
          for (const f of contextFieldsOf(detail.entry_form?.fields)) {
            if (f.field_type === "SCHOOL_MULTI") next[f.field_key] = saved.schools.map((x) => x.id);
            if (f.field_type === "CATEGORY") next[f.field_key] = saved.category ? saved.category.id : null;
            if (f.field_type === "SUBCATEGORY_MULTI") next[f.field_key] = saved.subcategories.map((x) => x.id);
            if (f.field_type === "USER_MULTI") next[f.field_key] = saved.feedback_recipients.map((x) => (x.user_id ? { user_id: x.user_id } : { email: x.email }));
          }
          setContext(next);
          setInitialRecipients(saved.feedback_recipients || []);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || "Could not load the entry form.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kpi.kpi_id, month]);

  const numberFields = useMemo(
    () => (form?.fields || []).filter((f) => ["NUMERATOR", "DENOMINATOR", "COUNT"].includes(f.role_in_calculation))
      .sort((a, b) => (a.role_in_calculation === "DENOMINATOR" ? -1 : b.role_in_calculation === "DENOMINATOR" ? 1 : 0)),
    [form]
  );

  const hasContext = contextFieldsOf(form?.fields).length > 0;

  const validationError = useMemo(() => {
    for (const f of numberFields) {
      const raw = values[f.field_key];
      if (raw === undefined || raw === "") return `Enter "${f.field_label}".`;
      if (!/^\d+(\.\d+)?$/.test(String(raw))) return `"${f.field_label}" must be a non-negative number.`;
    }
    const num = numberFields.find((f) => f.role_in_calculation === "NUMERATOR");
    const den = numberFields.find((f) => f.role_in_calculation === "DENOMINATOR");
    if (num && den && Number(values[num.field_key]) > Number(values[den.field_key])) {
      return `"${num.field_label}" cannot exceed "${den.field_label}".`;
    }
    return contextValidationError(form?.fields, context);
  }, [numberFields, values, form, context]);

  const preview = useMemo(() => {
    const num = numberFields.find((f) => f.role_in_calculation === "NUMERATOR");
    const den = numberFields.find((f) => f.role_in_calculation === "DENOMINATOR");
    if (!num || !den || validationError) return null;
    const d = Number(values[den.field_key]);
    return d > 0 ? Math.round((Number(values[num.field_key]) / d) * 10000) / 100 : null;
  }, [numberFields, values, validationError]);

  // The backend's own reason ({ message, code }, plus `details` outside
  // production), prefixed with the step that failed -- never only a generic
  // "Could not save". A network failure (no response) says so.
  const describeError = (err, step) => {
    const data = err?.response?.data || {};
    const reason = data.message || data.error
      || (err?.response ? `Request failed (HTTP ${err.response.status})` : "The server could not be reached");
    const extra = [data.code, typeof data.details === "string" ? data.details : null].filter(Boolean).join(" · ");
    return `${step}: ${reason}${extra ? ` [${extra}]` : ""}`;
  };

  const save = async () => {
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError("");
    let step = "Saving the entry failed";
    try {
      const payload = {
        month,
        values: Object.fromEntries(numberFields.map((f) => [f.field_key, Number(values[f.field_key])])),
        notes: notes.trim() || undefined,
        evidence_description: evidenceDescription.trim() || undefined,
        ...(hasContext ? { context } : {}),
        // With attachments, keep it a draft until the files are uploaded --
        // the evidence workflow only accepts files on a draft.
        submit: files.length === 0,
      };
      const res = await api.post(`/performance/my-kra-kpi/${kpi.kpi_id}/manual-evidence`, payload);
      const evidence = res.data?.data;
      if (files.length && evidence?.id) {
        for (const file of files) {
          // The entry is already saved as a draft at this point -- retrying
          // updates that same draft, never a second one.
          step = `Uploading "${file.name}" failed (your entry is saved as a draft)`;
          const fd = new FormData();
          fd.append("file", file);
          if (evidenceDescription.trim()) fd.append("description", evidenceDescription.trim());
          await api.post(`/performance/my-kra-kpi/evidence/${evidence.id}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        step = "Submitting for review failed (your entry and files are saved as a draft)";
        await api.post(`/performance/my-kra-kpi/evidence/${evidence.id}/submit`);
      }
      onSaved?.();
    } catch (err) {
      setError(describeError(err, step));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered size="lg" className="kp-modal kp-entry-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="div">
          <div className="tlp-eyebrow">Enter monthly data · {formatMonthKey(month)}</div>
          <div className="kp-modal-title">{kpi.kpi_name}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="kp-center"><Spinner animation="border" size="sm" /> Loading…</div>
        ) : !form?.supported ? (
          <p className="kp-muted mb-0">{form?.reason || "This KPI does not take monthly entries."}</p>
        ) : (
          <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
            {/* Two fields per line on tablet/desktop, one per line on phones. */}
            <Row className="g-3 mb-3">
            {numberFields.map((f) => (
              <Form.Group as={Col} md={6} controlId={`kp-field-${f.field_key}`} key={f.field_key}>
                <Form.Label>{f.field_label} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number" inputMode="decimal" min="0" step="any"
                  value={values[f.field_key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
                />
              </Form.Group>
            ))}
            {preview !== null && <Col xs={12}><p className="kp-sub mb-0">This month: {preview}%</p></Col>}
            {hasContext && (
              <EntryContextFields kpiId={kpi.kpi_id} month={month} fields={form.fields} value={context} onChange={setContext} initialRecipients={initialRecipients} />
            )}
            <Form.Group as={Col} md={6} controlId="kp-attachments">
              <Form.Label>Attachments</Form.Label>
              <Form.Control type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              <Form.Text muted>PDF, image, Word, Excel or text — up to 10 MB each.</Form.Text>
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="kp-notes">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="kp-evidence-description">
              <Form.Label>Evidence description</Form.Label>
              <Form.Control as="textarea" rows={3} value={evidenceDescription} onChange={(e) => setEvidenceDescription(e.target.value)} maxLength={2000}
                placeholder="What supports these numbers (report name, tracker, email…)" />
            </Form.Group>
            </Row>
            <p className="kp-sub mb-0">
              Your entry is sent to your reviewer and counts toward your score once approved.
              {form.feedback_enabled && " Submitting also sends this month's User Feedback form to the selected recipients (once per month — resubmitting never sends it twice)."}
            </p>
            {/* Recipients are asked for, but no feedback form is mapped to this
                KPI yet: the entry still submits normally; the feedback request
                simply starts once an administrator configures the form. */}
            {!form.feedback_enabled && contextFieldsOf(form.fields).some((f) => f.field_type === "USER_MULTI") && (
              <p className="kp-sub mb-0 mt-1" data-testid="kp-feedback-pending">
                Feedback configuration pending: the recipients are saved with this entry, and the feedback form will be sent once an administrator sets it up.
              </p>
            )}
          </Form>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="tlp-btn tlp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        {form?.supported && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={save} disabled={saving || !!validationError}>
            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : "Submit for review"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ManualEntryModal;
